'use client'
import type { FC } from 'react'
import { useEffect, useMemo, useState } from 'react'
import type { StackProps } from '@chakra-ui/react'
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  HStack,
  Text,
  VStack,
  Wrap,
  WrapItem
} from '@chakra-ui/react'
import { MdOutlineFilterList } from 'react-icons/md'
import { EmptyMessage, GrandText } from 'ui'
import { createCn } from 'shared-utils/utils'
import type { Option, UIVariant, UIVariantOption } from 'ecosystem'
import { useUIGlobalsContext } from 'ui/lib/ui-globals/hooks'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import useVariants from '../../hooks/useVariants'
import VariantItem from './VariantItem'

const cn = createCn('variant-picker')

type VariantPickerProps = StackProps & {
  variantGroupId: Option<string>
  onSelectVariant: (variant: UIVariant) => void
  isVariantSelected: (variant: UIVariant) => boolean
  onVariantsUpdate: (variantGroupId: string, variants: Option<UIVariant[]>) => void
  isOptional?: boolean
  label?: Option<string>
  isCheckingInStock?: boolean
}

export interface VariantPickerOverrides {
  variantNotFound?: string
  showMoreBtnLabel?: string
  variants?: string
}

const PRODUCT_VARIANT_COLS = 4

const VariantPicker: FC<VariantPickerProps> = ({
  variantGroupId,
  onSelectVariant,
  isVariantSelected,
  onVariantsUpdate,
  isOptional,
  label,
  isCheckingInStock,
  ...props
}) => {
  const { badgeSize } = useUIGlobalsContext()
  const overrides = useOverridesContext<keyof VariantPickerOverrides>()
  const { unavailableUiVariants, variants, error, optionGroups, selectOption, isOptionSelected } =
    useVariants(variantGroupId, isCheckingInStock)
  const [showAll, setShowAll] = useState(false)

  const isShowMoreVisible = useMemo(() => {
    if (!variants) return false
    return variants.length > PRODUCT_VARIANT_COLS - 1 && !showAll
  }, [showAll, variants])

  useEffect(() => {
    if (!variantGroupId) return
    onVariantsUpdate(variantGroupId, variants)
  }, [onVariantsUpdate, variantGroupId, variants])

  if (error && !variants) {
    return <EmptyMessage color="status.error" text="Kunde inte hitta produkter efter variant" />
  }

  if (!variants) {
    return null
  }

  const textOptional = label ? ' (valfria)' : 'valfria'

  return (
    <VStack as="fieldset" className="variant-picker" minH={10} spacing={4} w="100%" {...props}>
      {label || isOptional ? (
        <Box as="legend" mx={-1} px="1">
          {label ? (
            <Text as="span" color="gray.700" fontSize="md">
              {label}
            </Text>
          ) : null}

          {isOptional ? (
            <Text as="span" color="gray.400" fontSize="sm" fontWeight="bold">
              {textOptional}
            </Text>
          ) : null}
        </Box>
      ) : null}

      <VStack
        align="flex-start"
        className={cn('variants-wrapper')}
        justify="flex-start"
        spacing={0}
        w="full"
      >
        <HStack className={cn('available-variants')} spacing={0} w="full" wrap="wrap">
          {variants.length ? (
            variants.map((variant, index) => {
              if (!showAll && index >= PRODUCT_VARIANT_COLS - 1) {
                return null
              }
              return (
                <VariantItem
                  columns={PRODUCT_VARIANT_COLS}
                  isAvailable
                  isSelected={isVariantSelected(variant)}
                  item={variant}
                  key={variant.id}
                  onClick={(e) => {
                    e.preventDefault()
                    onSelectVariant(variant)
                  }}
                />
              )
            })
          ) : (
            <EmptyMessage
              className={cn('empty-message')}
              h={{ base: '66px', sm: '90px', md: '66px', lg: '74px' }}
              text={overrideText('Variant hittades inte', overrides?.variantNotFound)}
            />
          )}

          {isShowMoreVisible ? (
            <Button
              aria-label={`show ${variants.length - PRODUCT_VARIANT_COLS + 1} more`}
              borderRadius="sm"
              color="text.mild"
              fontSize="xs"
              fontWeight={400}
              h={{ base: '66px', sm: '90px', md: '66px', lg: '74px' }}
              onClick={() => {
                setShowAll(true)
              }}
              variant="ghost"
              w={`${100 / PRODUCT_VARIANT_COLS}%`}
              whiteSpace="pre-wrap"
            >
              {variants.length - PRODUCT_VARIANT_COLS + 1}{' '}
              {overrideText('mer', overrides?.showMoreBtnLabel)}
            </Button>
          ) : null}
        </HStack>

        <HStack className={cn('unavailable-variants')} w="full">
          {unavailableUiVariants?.map((variant) => (
            <VariantItem
              columns={PRODUCT_VARIANT_COLS}
              isAvailable={false}
              isSelected={false}
              item={variant}
              key={variant.id}
              onClick={(e) => {
                e.preventDefault()
                onSelectVariant(variant)
              }}
            />
          ))}
        </HStack>
      </VStack>

      {Boolean(optionGroups.length) && (
        <Accordion allowToggle w="full" className={cn('option-groups')}>
          <AccordionItem borderBottom="none" borderTop="none">
            <AccordionButton
              _focus={{ outline: 'none' }}
              aria-label="options"
              px={0}
              gap={2}
              fontSize="sm"
            >
              <MdOutlineFilterList />
              <Text>{overrideText('Varianter', overrides?.variants)}</Text>
            </AccordionButton>
            <AccordionPanel borderColor="gray.200" borderTop="1px solid" px={0}>
              {optionGroups.map((optionGroup, index) => (
                // eslint-disable-next-line react/no-array-index-key  -- No ID available
                <VStack fontSize="xs" key={`option-group${index}`} spacing={1} w="full">
                  <GrandText color="text.mild">{optionGroup.displayName}</GrandText>

                  <Wrap w="100%">
                    {optionGroup.options.map((option: UIVariantOption, optionIndex) => (
                      <WrapItem
                        borderColor={isOptionSelected(option) ? 'primary' : 'transparent'}
                        borderRadius="sm"
                        borderWidth="2px"
                        cursor="pointer"
                        // eslint-disable-next-line react/no-array-index-key  -- No ID available
                        key={`option-group-option-${optionIndex}`}
                        onClick={() => {
                          selectOption(option)
                        }}
                        p={badgeSize}
                        whiteSpace="nowrap"
                      >
                        {option.value}
                      </WrapItem>
                    ))}
                  </Wrap>
                </VStack>
              ))}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}
    </VStack>
  )
}

export default VariantPicker
